import * as http from 'utils/http';
import config from 'config';
import { select, takeEvery, call, put, takeLatest } from 'redux-saga/effects';
import { sendEventToSegment } from 'redux/modules/analytics';
import {
  JOURNEY_MAP_OPEN_DRIVER_INFO,
  JOURNEY_MAP_OPEN,
  JOURNEY_MAP_GOOGLE_MAPS_CLICK,
} from 'redux/modules/analytics/events';

import { getDriver, getGPSNow } from 'redux/modules/driver-position/http';
import { mapJourneyForComputedProperties } from 'redux/modules/journeys/computed';
import {
  lastDriverPosition as lastDriverPositionRequest,
  assignJourneyWithId,
} from 'redux/modules/journeys/http';
import { vehiclesIncreasingBySize } from 'utils/sort';

import { getItem } from './selectors';
import {
  driverInfoInitialState,
  setFetching,
  setDriverInfo,
  setPositions,
  setOpened,
  setData,
} from './duck';

const openMapModal$ = 'journey-map/openMapModal$';
const closeMapModal$ = 'journey-map/closeMapModal$';
const fetchPositions$ = 'journey-map/fetchPositions$';
const openDriverInfo$ = 'journey-map/openDriverInfo$';
const closeDriverInfo$ = 'journey-map/closeDriverInfo$';
const assignDriver$ = 'journey-map/assignDriver$';
const mapMounted$ = 'journey-map/mapMounted$';
const mapUpdatedParameters$ = 'journey-map/mapUpdatedParameters';

export const openJourneyMapModal = (payload) => ({
  type: openMapModal$,
  payload,
});

export const closeJourneyMapModal = (payload) => ({
  type: closeMapModal$,
  payload,
});

export const fetchPositions = (payload) => ({
  type: fetchPositions$,
  payload,
});

export const openDriverInfo = (payload) => ({
  type: openDriverInfo$,
  payload,
});

export const closeDriverInfo = (payload) => ({
  type: closeDriverInfo$,
  payload,
});

export const assignDriver = (payload) => ({
  type: assignDriver$,
  payload,
});

export const mapMounted = (payload) => ({
  type: mapMounted$,
  payload,
});

export const mapUpdatedParameters = (payload) => ({
  type: mapUpdatedParameters$,
  payload,
});

const computeJourney = async (journey) => mapJourneyForComputedProperties(journey);

const computedObject = {
  journey: computeJourney,
  shipperorder: (so) => so,
  NOT_FOUND: (so) => so,
};

const isNextStop = ({ status }) => ['PENDING', 'IN_PROGRESS'].includes(status);

const getGoogleMapRouteURL = (lastPosition, index, stopAddresses) => {
  const position = lastPosition && lastPosition.data && lastPosition.data[0];
  const showPosition = position && index >= 0;

  return (
    showPosition
      ? [
          ...stopAddresses.slice(0, index),
          `${position.latitude},${position.longitude}`,
          ...stopAddresses.slice(index),
        ]
      : stopAddresses
  ).join('/');
};

function* handleGetPositions({ journeyId, bounds, vehicleTypeFilter = [] }) {
  if (!journeyId || !bounds) {
    return;
  }

  yield put(setFetching({ model: 'positions', fetching: true }));
  try {
    const positions = yield call(getGPSNow);

    const filteredPositions = positions.filter((position) => {
      const googlePosition = new window.google.maps.LatLng(position.latitude, position.longitude);
      return bounds.contains(googlePosition);
    });

    const driver_ids = filteredPositions.map(({ driver_id }) => driver_id);

    const { capable_drivers } = yield call(
      http.post,
      `${config.API_URL}mercury/journeys/${journeyId}/can-do-the-job`,
      vehicleTypeFilter.length ? { driver_ids, vehicle_types: vehicleTypeFilter } : { driver_ids }
    );

    const drivers = (capable_drivers || []).reduce(
      (acc, driver) => ({ ...acc, [driver.id]: driver.available }),
      {}
    );

    const suitableDrivers = filteredPositions
      .filter(({ driver_id }) => drivers.hasOwnProperty(driver_id))
      .map((position) => ({ ...position, available: drivers[position.driver_id] }));

    yield put(setPositions(suitableDrivers));
  } catch (err) {
    yield call(http.getError, err);
    console.error(err);
  } finally {
    yield put(setFetching({ model: 'positions', fetching: false }));
  }
}

function* openMapModal({ payload }) {
  const {
    item: { id, url },
    idType,
  } = payload;

  yield put(setOpened({ opened: true, idType }));

  const item = yield call(http.get, url ? url : `${config.API_URL}mercury/journeys/${id}/`);
  const computeFn = computedObject[idType] ? computedObject[idType] : computedObject.NOT_FOUND;
  const computedItem = yield call(computeFn, item);
  yield put(setData(computedItem));
}

function* handleMapMounted({ payload }) {
  yield call(handleGetPositions, payload);
}

const openGoogleMaps = (win, lastDriverPosition = null, stops) => {
  const positionIndex = stops.findIndex(isNextStop);
  const stopAddresses = stops.map(({ address }) => {
    if (address.latitude && address.longitude) {
      return `${address.latitude},${address.longitude}`;
    }

    return `${address.zipcode}+${address.city}`;
  });
  win.location = `https://www.google.com/maps/dir/${getGoogleMapRouteURL(
    lastDriverPosition,
    positionIndex,
    stopAddresses
  )}`;
};

function* handleOpenGoogleMaps({ payload }) {
  const { item } = payload;
  const { driver, stops = [] } = item;
  const { user_set } = driver || {} || [];
  const user = (user_set || [])[0];
  const newWindow = yield call(window.open);

  try {
    const lastDriverPosition = yield call(lastDriverPositionRequest, user.id);
    yield call(openGoogleMaps, newWindow, lastDriverPosition, stops);
  } catch (err) {
    yield call(openGoogleMaps, newWindow, null, stops);
  }
}

function* closeMapModal() {
  yield put(setData(null));
  yield put(setPositions([]));
  yield put(setDriverInfo({ ...driverInfoInitialState }));
  yield put(setFetching({ model: 'positions', fetching: false }));
}

const openCustomMapStatuses = ['PENDING_MODERATION', 'PENDING_ASSIGNMENT'];

function* handleOpenMap({ payload }) {
  const { item, openCustomMap = false, forceGoogleMaps } = payload;
  const { start_time_from, status, id } = item;

  if (openCustomMap || (!forceGoogleMaps && openCustomMapStatuses.includes(status))) {
    yield call(sendEventToSegment, JOURNEY_MAP_OPEN, {
      journeyId: id,
      journeyStatus: status,
      journeyStartTimeFrom: start_time_from,
      timestamp: new Date().getTime(),
    });
    yield call(openMapModal, { payload });
    return;
  }

  yield call(sendEventToSegment, JOURNEY_MAP_GOOGLE_MAPS_CLICK, {
    journeyId: id,
    journeyStatus: status,
    journeyStartTimeFrom: start_time_from,
    isForced: forceGoogleMaps,
    timestamp: new Date().getTime(),
  });
  yield call(handleOpenGoogleMaps, { payload });
}

function* handleOpenDriverInfo({ payload }) {
  const { driver_id, location_mode, timestamp } = payload;

  yield put(setDriverInfo({ ...driverInfoInitialState }));
  yield put(setDriverInfo({ opened: true, loading: true, driverData: null, positionData: null }));

  const item = yield select(getItem);
  const { id, status, start_time_from } = item;

  sendEventToSegment(JOURNEY_MAP_OPEN_DRIVER_INFO, {
    journeyId: id,
    journeyStatus: status,
    journeyStartTimeFrom: start_time_from,
    timestamp: new Date().getTime(),
  });

  try {
    const driver = yield call(getDriver, driver_id);

    const { vehicles } = driver;

    yield put(
      setDriverInfo({
        driverData: {
          ...driver,
          vehicles: vehicles.sort(vehiclesIncreasingBySize),
        },
        positionData: {
          locationMode: location_mode,
          timestamp,
        },
        loading: false,
      })
    );
  } catch (err) {
    yield put(
      setDriverInfo({
        opened: false,
        driverData: null,
        positionData: null,
        loading: false,
        error: err,
      })
    );
  }
}

function* handleCloseDriverInfo() {
  yield put(setDriverInfo({ ...driverInfoInitialState }));
}

function* handleDriverAssignment({ payload }) {
  const driverId = payload;
  const item = yield select(getItem);

  yield put(setDriverInfo({ assigning: true }));

  try {
    yield call(assignJourneyWithId, item, driverId);
    yield put(setDriverInfo({ assignDone: true }));
  } catch (err) {
    yield put(setDriverInfo({ assignError: err }));
  } finally {
    yield put(setDriverInfo({ assigning: false }));
  }
}

export function* watchJourneyMap() {
  yield takeLatest(openMapModal$, handleOpenMap);
  yield takeLatest(closeMapModal$, closeMapModal);
  yield takeLatest(fetchPositions$, handleGetPositions);
  yield takeEvery(mapMounted$, handleMapMounted);
  yield takeEvery(mapUpdatedParameters$, handleMapMounted);

  yield takeLatest(openDriverInfo$, handleOpenDriverInfo);
  yield takeLatest(assignDriver$, handleDriverAssignment);
  yield takeLatest(closeDriverInfo$, handleCloseDriverInfo);
}
